// "How the widget estimates heat pump size and price": the estimate terms an
// installer reads and accepts for a widget, shown in the new-widget wizard
// (step 2) and on the Install tab until accepted. Acceptance is recorded per
// widget and version by the console API (functions/terms). Solar estimates show
// and accept their own terms (SOLAR_TERMS, with its own version): pass product.
import { useState } from "react";
import { useApi, type TermsStatus } from "../api";
import { termsFor } from "../terms";
import { Attention } from "./Attention";

export function TermsCard({
  widgetId,
  intro,
  buttonText,
  onAccepted,
  guide = false,
  product,
}: {
  widgetId: string;
  intro: string;
  buttonText: string;
  onAccepted: (status: TermsStatus) => void;
  guide?: boolean; // show the pulsing star on the next thing to do (wizard)
  product?: "ashp" | "solar"; // which terms: heat pump (default) or solar estimate
}) {
  const TERMS = termsFor(product);
  const api = useApi();
  const [agreed, setAgreed] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function accept() {
    setAccepting(true);
    setError(null);
    try {
      onAccepted(await api.acceptTerms(widgetId, TERMS.version));
    } catch (e) {
      setError((e as Error).message);
      setAccepting(false);
    }
  }

  return (
    <section className="card terms-card">
      <h2>{TERMS.title}</h2>
      <p className="card-sub">{intro}</p>
      <div className="terms-text" tabIndex={0} aria-label="Estimate terms">
        {TERMS.sections.map((s) => (
          <div key={s.heading}>
            <h3>{s.heading}</h3>
            {s.body.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </div>
        ))}
        <p className="terms-version">Version {TERMS.version}</p>
      </div>
      <label className="terms terms-accept">
        <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
        <span>
          {TERMS.acceptLabel}
          {/* At the end of the line, clear of the checkbox */}
          {guide && !agreed && <Attention corner="inline" label="Read and tick to accept" />}
        </span>
      </label>
      {error && <div className="flash flash-danger">{error}</div>}
      <span className="has-attention">
        <button type="button" className="btn btn-primary" disabled={!agreed || accepting} onClick={() => void accept()}>
          {accepting ? "Recording…" : buttonText}
        </button>
        {guide && agreed && !accepting && <Attention corner="tr" />}
      </span>
    </section>
  );
}
